<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import { auth } from "$lib/stores/auth.svelte";
  import { api } from "$lib/api/client";
  import { fmtBRL } from "$lib/utils/format";
  import { toasts } from "$lib/stores/toasts.svelte";

  interface Contract {
    id: string;
    name: string;
    provider: string | null;
    notes: string | null;
    monthly_amount: number | null;
    due_day: number | null;
    start_date: string;
    total_months: number;
    end_date: string;
    months_paid: number;
    months_remaining: number;
    progress_pct: number;
    next_due_date: string | null;
    total_paid: number;
    total_contract: number;
    status: string;
  }

  // ── Estado ────────────────────────────────────────────────────────────────
  let contracts   = $state<Contract[]>([]);
  let loading     = $state(true);
  let formOpen    = $state(false);
  let editingId   = $state<string | null>(null);
  let saving      = $state(false);
  let deleting    = $state<string | null>(null);
  let reportOpen  = $state(false);

  // Formulário
  let fName          = $state("");
  let fProvider      = $state("");
  let fStartDate     = $state("");
  let fTotalMonths   = $state(12);
  let fMonthlyAmount = $state("");
  let fDueDay        = $state("");
  let fNotes         = $state("");
  let fStatus        = $state("active");

  // ── API ──────────────────────────────────────────────────────────────────
  async function load() {
    loading = true;
    try {
      contracts = await api<Contract[]>("/loyalty");
    } catch { contracts = []; }
    finally { loading = false; }
  }

  $effect(() => { if (auth.workspaces[0]) load(); });
  $effect(() => {
    function onWs() { load(); }
    window.addEventListener("takite:workspace-changed", onWs);
    return () => window.removeEventListener("takite:workspace-changed", onWs);
  });

  function openNew() {
    editingId = null;
    fName = ""; fProvider = ""; fNotes = ""; fStatus = "active";
    fStartDate = new Date().toISOString().slice(0, 10);
    fTotalMonths = 12; fMonthlyAmount = ""; fDueDay = "";
    formOpen = true;
  }

  function openEdit(c: Contract) {
    editingId = c.id;
    fName = c.name;
    fProvider = c.provider ?? "";
    fNotes = c.notes ?? "";
    fStartDate = c.start_date;
    fTotalMonths = c.total_months;
    fMonthlyAmount = c.monthly_amount != null ? String(c.monthly_amount) : "";
    fDueDay = c.due_day != null ? String(c.due_day) : "";
    fStatus = c.status;
    formOpen = true;
  }

  async function save(e: Event) {
    e.preventDefault();
    saving = true;
    const body = {
      name: fName.trim(),
      provider: fProvider.trim() || null,
      notes: fNotes.trim() || null,
      start_date: fStartDate,
      total_months: Number(fTotalMonths),
      monthly_amount: fMonthlyAmount ? Number(fMonthlyAmount) : null,
      due_day: fDueDay ? Number(fDueDay) : null,
      status: fStatus,
    };
    try {
      if (editingId) {
        const updated = await api<Contract>(`/loyalty/${editingId}`, { method: "PATCH", body: JSON.stringify(body) });
        contracts = contracts.map(c => c.id === editingId ? updated : c);
        toasts.success("Contrato atualizado");
      } else {
        const created = await api<Contract>("/loyalty", { method: "POST", body: JSON.stringify(body) });
        contracts = [created, ...contracts];
        toasts.success("Contrato adicionado");
      }
      formOpen = false;
    } catch (err: any) {
      toasts.error("Erro ao salvar", err?.body?.detail ?? err?.message);
    } finally { saving = false; }
  }

  async function remove(id: string, name: string) {
    if (!confirm(`Excluir o contrato "${name}"?`)) return;
    deleting = id;
    try {
      await api(`/loyalty/${id}`, { method: "DELETE" });
      contracts = contracts.filter(c => c.id !== id);
      toasts.success("Contrato removido");
    } catch (err: any) {
      toasts.error("Erro", err?.body?.detail ?? err?.message);
    } finally { deleting = null; }
  }

  // ── Helpers de UI ─────────────────────────────────────────────────────────
  const STATUS_LABEL: Record<string, string> = {
    active: "Ativo", cancelled: "Cancelado", expired: "Encerrado"
  };
  const STATUS_COLOR: Record<string, string> = {
    active: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    expired: "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300",
  };

  function fmtDate(iso: string) {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  }

  function daysUntil(iso: string | null) {
    if (!iso) return null;
    const diff = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
    return diff;
  }

  // Relatório: totais
  const activeContracts = $derived(contracts.filter(c => c.status === "active"));
  const totalMonthly    = $derived(activeContracts.reduce((s, c) => s + (c.monthly_amount ?? 0), 0));
  const totalRemaining  = $derived(activeContracts.reduce((s, c) => s + (c.monthly_amount ?? 0) * c.months_remaining, 0));
</script>

<Navbar />
<main class="max-w-4xl mx-auto px-4 py-6 space-y-6">

  <!-- Cabeçalho -->
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-bold">📋 Fidelidades e Contratos</h1>
      <p class="text-sm text-slate-500 mt-0.5">Acompanhe planos, contratos e fidelizações com prazo definido</p>
    </div>
    <div class="flex gap-2">
      <button class="btn-ghost text-sm" onclick={() => reportOpen = !reportOpen}>
        {reportOpen ? "Fechar relatório" : "📊 Relatório"}
      </button>
      <button class="btn-primary" onclick={openNew}>+ Novo contrato</button>
    </div>
  </div>

  <!-- Relatório resumido -->
  {#if reportOpen}
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="card text-center">
        <div class="text-3xl font-bold text-brand">{activeContracts.length}</div>
        <div class="text-sm text-slate-500 mt-1">Contratos ativos</div>
      </div>
      <div class="card text-center">
        <div class="text-3xl font-bold">{fmtBRL(totalMonthly)}</div>
        <div class="text-sm text-slate-500 mt-1">Gasto mensal com fidelidades</div>
      </div>
      <div class="card text-center">
        <div class="text-3xl font-bold text-amber-600">{fmtBRL(totalRemaining)}</div>
        <div class="text-sm text-slate-500 mt-1">Comprometido até fim dos contratos</div>
      </div>
    </div>

    <!-- Tabela relatório detalhada -->
    {#if activeContracts.length > 0}
      <div class="card overflow-x-auto">
        <h2 class="font-semibold mb-3">Contratos vigentes — visão geral</h2>
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-slate-400 text-xs border-b dark:border-slate-700">
              <th class="pb-2 pr-4">Contrato</th>
              <th class="pb-2 pr-4">Mensalidade</th>
              <th class="pb-2 pr-4">Progresso</th>
              <th class="pb-2 pr-4">Meses restantes</th>
              <th class="pb-2 pr-4">Término</th>
              <th class="pb-2">Total restante</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100 dark:divide-slate-800">
            {#each activeContracts.sort((a, b) => a.months_remaining - b.months_remaining) as c}
              <tr>
                <td class="py-2 pr-4">
                  <div class="font-medium">{c.name}</div>
                  {#if c.provider}<div class="text-xs text-slate-400">{c.provider}</div>{/if}
                </td>
                <td class="py-2 pr-4">{c.monthly_amount ? fmtBRL(c.monthly_amount) : "—"}</td>
                <td class="py-2 pr-4">
                  <div class="flex items-center gap-2">
                    <div class="w-24 h-2 rounded-full bg-slate-200 dark:bg-slate-700">
                      <div
                        class="h-2 rounded-full {c.progress_pct >= 80 ? 'bg-green-500' : c.progress_pct >= 40 ? 'bg-brand' : 'bg-amber-500'}"
                        style="width: {c.progress_pct}%"
                      ></div>
                    </div>
                    <span class="text-xs text-slate-400">{c.progress_pct}%</span>
                  </div>
                </td>
                <td class="py-2 pr-4">
                  <span class="{c.months_remaining <= 3 ? 'text-red-600 font-semibold' : c.months_remaining <= 6 ? 'text-amber-600' : ''}">
                    {c.months_remaining} meses
                  </span>
                </td>
                <td class="py-2 pr-4">{fmtDate(c.end_date)}</td>
                <td class="py-2">{c.monthly_amount ? fmtBRL(c.monthly_amount * c.months_remaining) : "—"}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  {/if}

  <!-- Lista de contratos -->
  {#if loading}
    <p class="text-slate-400 text-center py-12">Carregando…</p>
  {:else if contracts.length === 0}
    <div class="card text-center py-12 space-y-3">
      <p class="text-4xl">📋</p>
      <p class="font-medium">Nenhum contrato cadastrado ainda</p>
      <p class="text-sm text-slate-400">Adicione planos de celular, internet, academia, streaming com prazo de fidelidade</p>
      <button class="btn-primary" onclick={openNew}>+ Adicionar primeiro contrato</button>
    </div>
  {:else}
    <div class="space-y-3">
      {#each contracts as c}
        {@const days = daysUntil(c.next_due_date)}
        <div class="card space-y-3">
          <!-- Cabeçalho do card -->
          <div class="flex items-start justify-between gap-3">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="font-semibold text-base">{c.name}</span>
                {#if c.provider}
                  <span class="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                    {c.provider}
                  </span>
                {/if}
                <span class="text-xs px-2 py-0.5 rounded-full {STATUS_COLOR[c.status] ?? STATUS_COLOR.active}">
                  {STATUS_LABEL[c.status] ?? c.status}
                </span>
              </div>
              {#if c.notes}
                <p class="text-sm text-slate-400 mt-0.5 truncate">{c.notes}</p>
              {/if}
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <button class="btn-ghost text-sm p-1.5" title="Editar" onclick={() => openEdit(c)}>✏️</button>
              <button
                class="btn-ghost text-sm p-1.5 text-red-500"
                title="Excluir"
                disabled={deleting === c.id}
                onclick={() => remove(c.id, c.name)}
              >🗑️</button>
            </div>
          </div>

          <!-- Barra de progresso de fidelidade -->
          <div class="space-y-1">
            <div class="flex justify-between text-xs text-slate-400">
              <span>Início: {fmtDate(c.start_date)}</span>
              <span class="{c.months_remaining <= 3 ? 'text-red-600 font-semibold' : c.months_remaining <= 6 ? 'text-amber-600 font-semibold' : ''}">
                Término: {fmtDate(c.end_date)}
              </span>
            </div>
            <div class="w-full h-3 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
              <div
                class="h-3 rounded-full transition-all {c.progress_pct >= 80 ? 'bg-green-500' : c.progress_pct >= 40 ? 'bg-brand' : 'bg-amber-500'}"
                style="width: {c.progress_pct}%"
                title="{c.progress_pct}% cumprido"
              ></div>
            </div>
            <div class="flex justify-between text-xs">
              <span class="text-slate-500">{c.months_paid} de {c.total_months} meses pagos</span>
              <span class="{c.months_remaining <= 3 ? 'text-red-600 font-semibold' : c.months_remaining <= 6 ? 'text-amber-500 font-semibold' : 'text-slate-500'}">
                {c.months_remaining > 0 ? `${c.months_remaining} meses restantes` : "Fidelidade cumprida ✅"}
              </span>
            </div>
          </div>

          <!-- Métricas rápidas -->
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1 border-t dark:border-slate-700">
            <div>
              <div class="text-xs text-slate-400">Mensalidade</div>
              <div class="font-semibold">{c.monthly_amount ? fmtBRL(c.monthly_amount) : "—"}</div>
            </div>
            <div>
              <div class="text-xs text-slate-400">Total pago</div>
              <div class="font-semibold">{c.monthly_amount ? fmtBRL(c.total_paid) : "—"}</div>
            </div>
            <div>
              <div class="text-xs text-slate-400">Restante a pagar</div>
              <div class="font-semibold {c.months_remaining > 0 ? 'text-amber-600' : 'text-green-600'}">
                {c.monthly_amount && c.months_remaining > 0 ? fmtBRL(c.monthly_amount * c.months_remaining) : c.months_remaining === 0 ? "R$ 0,00" : "—"}
              </div>
            </div>
            <div>
              <div class="text-xs text-slate-400">Próx. vencimento</div>
              <div class="font-semibold {days !== null && days <= 5 ? 'text-red-600' : days !== null && days <= 10 ? 'text-amber-600' : ''}">
                {#if c.next_due_date}
                  {fmtDate(c.next_due_date)}
                  {#if days !== null && days === 0}
                    <span class="text-xs font-normal text-red-600"> • hoje</span>
                  {:else if days !== null && days > 0}
                    <span class="text-xs font-normal text-slate-400"> • em {days}d</span>
                  {/if}
                {:else}
                  —
                {/if}
              </div>
            </div>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</main>

<!-- ── Modal de formulário ──────────────────────────────────────────────── -->
{#if formOpen}
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true">
    <div class="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
      <div class="flex items-center justify-between p-5 border-b dark:border-slate-700">
        <h2 class="font-semibold text-lg">{editingId ? "Editar contrato" : "Novo contrato de fidelidade"}</h2>
        <button class="btn-ghost p-1" onclick={() => formOpen = false} aria-label="Fechar">✕</button>
      </div>

      <form onsubmit={save} class="p-5 space-y-4">

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div class="space-y-1 sm:col-span-2">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Nome do contrato *</label>
            <input class="input w-full" bind:value={fName} placeholder="Ex: Plano TIM Controle, Netflix, Academia" required />
          </div>

          <div class="space-y-1">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Fornecedor / Empresa</label>
            <input class="input w-full" bind:value={fProvider} placeholder="Ex: TIM, Vivo, NET, Smartfit" />
          </div>

          <div class="space-y-1">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Status</label>
            <select class="input w-full" bind:value={fStatus}>
              <option value="active">Ativo</option>
              <option value="cancelled">Cancelado</option>
              <option value="expired">Encerrado</option>
            </select>
          </div>

          <div class="space-y-1">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Data de início *</label>
            <input class="input w-full" type="date" bind:value={fStartDate} required />
          </div>

          <div class="space-y-1">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Fidelidade (meses) *</label>
            <input class="input w-full" type="number" min="1" max="360" bind:value={fTotalMonths} required />
            <p class="text-xs text-slate-400">Ex: 12, 18, 24 meses</p>
          </div>

          <div class="space-y-1">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Mensalidade (R$)</label>
            <input class="input w-full" type="number" min="0" step="0.01" bind:value={fMonthlyAmount} placeholder="0,00" />
          </div>

          <div class="space-y-1">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Dia do vencimento</label>
            <input class="input w-full" type="number" min="1" max="31" bind:value={fDueDay} placeholder="Ex: 20" />
            <p class="text-xs text-slate-400">Dia do mês que vence a mensalidade</p>
          </div>

          <div class="space-y-1 sm:col-span-2">
            <label class="text-xs font-medium text-slate-500 uppercase tracking-wide">Observações</label>
            <textarea class="input w-full h-20 resize-none" bind:value={fNotes} placeholder="Detalhes, condições especiais, etc."></textarea>
          </div>
        </div>

        <!-- Preview calculado -->
        {#if fStartDate && fTotalMonths}
          {@const endPreview = (() => {
            const d = new Date(fStartDate + "T12:00:00");
            d.setMonth(d.getMonth() + Number(fTotalMonths));
            return d.toLocaleDateString("pt-BR");
          })()}
          <div class="rounded-lg bg-slate-50 dark:bg-slate-700/50 p-3 text-sm space-y-1">
            <p class="font-medium text-slate-600 dark:text-slate-300">📅 Prévia</p>
            <p class="text-slate-500">Término da fidelidade: <strong>{endPreview}</strong></p>
            {#if fMonthlyAmount}
              <p class="text-slate-500">Total do contrato: <strong>{fmtBRL(Number(fMonthlyAmount) * Number(fTotalMonths))}</strong></p>
            {/if}
          </div>
        {/if}

        <div class="flex gap-2 pt-2">
          <button type="button" class="btn-ghost flex-1" onclick={() => formOpen = false}>Cancelar</button>
          <button type="submit" class="btn-primary flex-1" disabled={saving}>
            {saving ? "Salvando…" : editingId ? "Salvar alterações" : "Adicionar contrato"}
          </button>
        </div>
      </form>
    </div>
  </div>
{/if}
